import { useState, useEffect } from 'react';
import {
  Button,
  Slider,
  Tooltip,
  ModalWrapper,
  TextInput,
  Dropdown,
  ContentSwitcher,
  Switch,
  DataTable,
  DataTableCustomRenderProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from 'carbon-components-react';
import { Alignments } from '@carbon/charts/interfaces/enums';
import { FormattedMessage, useIntl } from 'react-intl';
import Cookies from 'js-cookie';
import './SterlingBVAROI.scss';
import Footnote from './Footnote';
import '@carbon/charts/styles.css';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import {
  removeSymbol,
  useCurrency,
} from 'src/common/hooks/widgets/useCurrency';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { DonutChart } from '@carbon/charts-react';

import {
  UT30SIPROI,
  medPct,
  lowPct,
  hightPct,
  estAnnualSale,
  onlinePctSale,
  industryAvg,
  markdownAvg,
  shippingAvg,
} from '../constants/defaults';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { setPrice as setROI } from '../hooks/useRoi';
import {
  determineCountry,
  LangDefaults,
  useLang,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { FormModal } from 'src/common/utils/FormModal';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { Helmet } from 'react-helmet';
import Thanks from './Thanks';

export interface SterlingBVAROIProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export interface inputType {
  estimatedAnnualSale: number;
  onlineSalePercentage: number;
  industryAvgProfit: number;
  avgMarkDownCost: number;
  avgShippingCost: number;
}

export const inputDefault: inputType = {
  estimatedAnnualSale: estAnnualSale.default,
  onlineSalePercentage: onlinePctSale.default,
  industryAvgProfit: 0,
  avgMarkDownCost: 0,
  avgShippingCost: 0,
};

export interface MaxMin {
  estimatedAnnualSaleMax: boolean;
  estimatedAnnualSaleMin: boolean;
  onlineSalePercentageMax: boolean;
  onlineSalePercentageMin: boolean;
  industryAvgProfitMax: boolean;
  industryAvgProfitMin: boolean;
  avgMarkDownCostMax: boolean;
  avgMarkDownCostMin: boolean;
  avgShippingCostMax: boolean;
  avgShippingCostMin: boolean;
}

export const MaxMinObject: MaxMin = {
  onlineSalePercentageMax: false,
  onlineSalePercentageMin: false,
  industryAvgProfitMax: false,
  industryAvgProfitMin: false,
  avgMarkDownCostMax: false,
  avgMarkDownCostMin: false,
  avgShippingCostMax: false,
  avgShippingCostMin: false,
  estimatedAnnualSaleMax: false,
  estimatedAnnualSaleMin: false,
};

interface IOption {
  id: string;
  label: string;
  profitMargin: number;
  markdownHigh: number;
  shippingCost: number;
  reduceOutOfStock: number;
  cookieValue: string;
}

const SterlingBVAROI = ({
  langCode,
  test,
  countryToTest,
}: SterlingBVAROIProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [urxlang] = useState(localeCode.toString());
  const [industry, setIndustry] = useState<IOption>({
    id: 'Q1A2',
    label: 'Clothing/Textiles',
    profitMargin: 0.3,
    markdownHigh: 0,
    shippingCost: 0.09,
    reduceOutOfStock: 0.5,
    cookieValue: 'Clothing/Textiles',
  });
  const [input, setInput] = useState(inputDefault);
  const [switchValue, setSwitchValue] = useState('Medium');
  const [invalidMaxMin, setInvalidMaxMin] = useState(MaxMinObject);
  const isSmall = useSmallBreakpoint('560px');
  const [rangeIndex, setRangeIndex] = useState(1);
  const [range, setRange] = useState(medPct);
  const [, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  // Set USD
  const [modalLocalCurrency, setModalLocalCurrency] = useState(
    LangDefaults.localCurrency,
  );
  // Set English language
  const [modalLocalLanguage, setModalLocalLanguage] = useState(
    LangDefaults.CountryLanguage,
  );
  const [increaseOnlineSalesTotal, setIncreaseOnlineSalesTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [increaseStoreSalesTotal, setIncreaseStoreSalesTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [reduceReturnsTotal, setReduceReturnsTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [reduceMarkDownCostTotal, setReduceMarkDownCostTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [reduceShippingCostTotal, setReduceShippingCostTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [estimatedSavings, setEstimatedSavings] = useCurrency(
    'USD',
    userLocale,
    0,
  );

  const [increaseInSaleTotal, setIncreaseInSaleTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [reductionInCostsTotal, setReductionInCostsTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );

  const [additionalProfitTotal, setAdditionalProfitTotal] = useCurrency(
    'USD',
    userLocale,
    0,
  );
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [environment] = useEnv();
  const [formComplete, setFormComplete] = useState(false);
  const { bpName, logo, companyId } = useBPName(UT30SIPROI, true);
  const intl = useIntl();
  const totalEstimateSaving = Number(
    removeSymbol('USD', estimatedSavings).replace(/,/g, ''),
  );

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    false,
  );

  /**
   * Call to start the pricing
   */
  setROI(
    input,
    setIncreaseOnlineSalesTotal,
    setIncreaseStoreSalesTotal,
    setReduceReturnsTotal,
    setReduceMarkDownCostTotal,
    setReduceShippingCostTotal,
    setEstimatedSavings,
    setIncreaseInSaleTotal,
    setReductionInCostsTotal,
    setAdditionalProfitTotal,
    range,
    rangeIndex,
  );

  /**
   * Define objects required for graph
   */
  const state = {
    data: [
      {
        group: intl.formatMessage(messages.catg1_text),
        value: Number(
          removeSymbol('USD', increaseOnlineSalesTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.catg2_text_short),
        value: Number(
          removeSymbol('USD', increaseStoreSalesTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.catg3_text),
        value: Number(
          removeSymbol('USD', reduceReturnsTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.catg4_text),
        value: Number(
          removeSymbol('USD', reduceMarkDownCostTotal).replace(/,/g, ''),
        ),
      },
      {
        group: intl.formatMessage(messages.catg5_text),
        value: Number(
          removeSymbol('USD', reduceShippingCostTotal).replace(/,/g, ''),
        ),
      },
    ],
    options: {
      resizable: true,
      legend: {
        alignment: Alignments.CENTER,
        clickable: false,
        truncation: {
          type: 'none',
        },
      },
      donut: {
        alignment: Alignments.CENTER,
        center: {
          label: intl.formatMessage(messages.additional_profit),
        },
      },
      height: '400px',
      toolbar: {
        enabled: false,
      },
    },
  };

  useEffect(() => {
    const parentElement = Array.from(
      document.getElementsByClassName(
        'layout-child spacer',
      ) as HTMLCollectionOf<HTMLElement>,
    )[0];
    const newDiv = document.createElement('div');
    newDiv.setAttribute('class', 'page_body_2');
    newDiv.innerText = intl.formatMessage(messages.additional_profit);
    newDiv.style.textAlign = 'center';
    parentElement.appendChild(newDiv);
  }, []);

  /**
   * Define validation of the Input field of the sliders
   */
  useEffect(() => {
    const saleId = document.getElementById('Sale-Id-input-for-slider');
    const industryId = document.getElementById('Industry-Id-input-for-slider');
    const markdownId = document.getElementById('Markdown-Id-input-for-slider');
    const shippingId = document.getElementById('Shipping-Id-input-for-slider');
    const annualSaleId = document.getElementById('AnnualSale-Id');

    const addListener = (
      id: HTMLElement | null,
      name: string,
      max: number,
      min: number,
    ) => {
      if (id !== null) {
        id.addEventListener('input', (e) => {
          if (e != null) {
            const element = e.currentTarget as HTMLInputElement;
            if (element.value !== '') {
              if (Number.parseInt(element.value) > max) {
                element.value = max.toString();
                setInvalidMaxMin({
                  ...invalidMaxMin,
                  [name + 'Max']: true,
                });
                setInput({
                  ...input,
                  [name]: max,
                });
              } else if (Number.parseInt(element.value) < min) {
                element.value = min.toString();
                setInvalidMaxMin({
                  ...invalidMaxMin,
                  [name + 'Min']: true,
                });
                setInput({
                  ...input,
                  [name]: min,
                });
              }
            } else {
              element.value = ' ';
            }
          }
        });
      }
    };

    /**
     * Online Sale Percentage
     */
    addListener(
      saleId,
      'onlineSalePercentage',
      onlinePctSale.max,
      onlinePctSale.min,
    );

    /**
     * Industry Average Profit
     */
    addListener(
      industryId,
      'industryAvgProfit',
      industryAvg.max,
      industryAvg.min,
    );

    /**
     * Average Markdown Cost
     */
    addListener(
      markdownId,
      'avgMarkDownCost',
      markdownAvg.max,
      markdownAvg.min,
    );
    /**
     * Average Shipping Cost
     */
    addListener(
      shippingId,
      'avgShippingCost',
      shippingAvg.max,
      shippingAvg.min,
    );
    /**
     * Estimated Annual Sale
     */
    addListener(
      annualSaleId,
      'estimatedAnnualSale',
      estAnnualSale.max,
      estAnnualSale.min,
    );
  });

  useEffect(() => {
    setCookie();
  }, [
    input,
    increaseOnlineSalesTotal,
    increaseStoreSalesTotal,
    reduceMarkDownCostTotal,
    reduceReturnsTotal,
    reduceShippingCostTotal,
    increaseInSaleTotal,
    reductionInCostsTotal,
    estimatedSavings,
  ]);

  const setCookie = () => {
    const cookieContent =
      ' Industry: ' +
      industry.cookieValue +
      ' | Estimated Annual Sales: ' +
      input.estimatedAnnualSale +
      ' | Percent of sales attributed to online/digital(%): ' +
      input.onlineSalePercentage +
      '| Industry Average Gross Profit Margin(%): ' +
      input.industryAvgProfit +
      ' | Average Markdown Costs(%): ' +
      input.avgMarkDownCost +
      ' | Average Shipping Costs(%): ' +
      input.avgShippingCost +
      ' | Online Sales Increase: ' +
      increaseOnlineSalesTotal +
      ' | Increaes in Store Sales/Out of Stock Reduction: ' +
      increaseStoreSalesTotal +
      ' | Returns Reduction: ' +
      reduceReturnsTotal +
      ' | Markdown Cost Reduction: ' +
      reduceMarkDownCostTotal +
      ' | Shipping Cost Reduction: ' +
      reduceShippingCostTotal +
      ' | Annual Pro-forma Benefit: ' +
      switchValue +
      ' | Increase in Sales: ' +
      increaseInSaleTotal +
      ' | Reduction in Costs: ' +
      reductionInCostsTotal +
      ' | Additional Profit: ' +
      additionalProfitTotal +
      ' | Total Estimated Benefits: ' +
      estimatedSavings;
    const text = JSON.stringify({
      source: URX_FORM.FORM_ID,
      // One field to provide all values (stringify)
      dcq: {
        Q_QRDATA: cookieContent,
        Q_ASSET_URL: window.location.href, // ISC - Asset URL
        Q_ASSET_NAME: 'IBM Sterling SIP ROI/BVA Benefits Estimator', // ISC - Asset Name
      },
    });
    Cookies.set('urxdcq', text);
  };

  const answers = [
    {
      id: 'Q1A1',
      label: 'Books/Music/Gifts',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.09,
      reduceOutOfStock: 0.5,
      cookieValue: 'Books/Music/Gifts',
    },
    {
      id: 'Q1A2',
      label: 'Clothing/Textiles',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.09,
      reduceOutOfStock: 0.5,
      cookieValue: 'Clothing/Textiles',
    },
    {
      id: 'Q1A3',
      label: 'Consumer Durables',
      profitMargin: 0.2,
      markdownHigh: 0,
      shippingCost: 0.09,
      reduceOutOfStock: 0.4,
      cookieValue: 'Consumer Durables',
    },
    {
      id: 'Q1A4',
      label: 'Cosmetics',
      profitMargin: 0.5,
      markdownHigh: 0,
      shippingCost: 0.07,
      reduceOutOfStock: 0.4,
      cookieValue: 'Cosmetics',
    },
    {
      id: 'Q1A5',
      label: 'Electronics',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.05,
      reduceOutOfStock: 0.4,
      cookieValue: 'Electronics',
    },
    {
      id: 'Q1A6',
      label: 'Eyeglasses',
      profitMargin: 0.5,
      markdownHigh: 0,
      shippingCost: 0.04,
      reduceOutOfStock: 0.3,
      cookieValue: 'Eyeglasses',
    },
    {
      id: 'Q1A7',
      label: 'Food',
      profitMargin: 0.12,
      markdownHigh: 0,
      shippingCost: 0.04,
      reduceOutOfStock: 0.3,
      cookieValue: 'Food',
    },
    {
      id: 'Q1A8',
      label: 'Footwear',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.09,
      reduceOutOfStock: 0.5,
      cookieValue: 'Footwear',
    },
    {
      id: 'Q1A9',
      label: 'Furniture',
      profitMargin: 0.35,
      markdownHigh: 0,
      shippingCost: 0.04,
      reduceOutOfStock: 0.4,
      cookieValue: 'Furniture',
    },
    {
      id: 'Q1A10',
      label: 'Jewelry',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.04,
      reduceOutOfStock: 0.3,
      cookieValue: 'Jewelry',
    },
    {
      id: 'Q1A11',
      label: 'Specialty Items',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.09,
      reduceOutOfStock: 0.5,
      cookieValue: 'Specialty Items',
    },
    {
      id: 'Q1A12',
      label: 'Super Stores',
      profitMargin: 0.2,
      markdownHigh: 0,
      shippingCost: 0.09,
      reduceOutOfStock: 0.4,
      cookieValue: 'Super Stores',
    },
    {
      id: 'Q1A13',
      label: 'Other',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.05,
      reduceOutOfStock: 0.4,
      cookieValue: 'Other',
    },
    {
      id: 'Q1A14',
      label: 'Telco',
      profitMargin: 0.3,
      markdownHigh: 0,
      shippingCost: 0.05,
      reduceOutOfStock: 0.4,
      cookieValue: 'Telco',
    },
  ];

  const handleDropdown = (value: IOption) => {
    setIndustry({
      label: value.label,
      id: value.id,
      profitMargin: value.profitMargin,
      shippingCost: value.shippingCost,
      reduceOutOfStock: value.reduceOutOfStock,
      markdownHigh: value.markdownHigh,
      cookieValue: value.cookieValue,
    });
    handleSliders(
      estAnnualSale.default,
      estAnnualSale.max,
      estAnnualSale.min,
      'estimatedAnnualSale',
    );
  };

  const headers = [
    {
      key: 'category',
      header: 'Category',
    },
    {
      key: 'col2',
      header: '',
    },
    {
      key: 'range',
      header: switchValue,
    },
  ];

  const rows = [
    {
      id: 'row1',
      category: intl.formatMessage(messages.dt_row1_text),
      range: `${range.incOnlineSales * 100}%`,
    },
    {
      id: 'row2',
      category: intl.formatMessage(messages.dt_row2_text),
      range: `${range.incStoreSaleReduce * 100}%`,
    },
    {
      id: 'row3',
      category: intl.formatMessage(messages.dt_row3_text),
      range: `${range.reduceReturn * 100}%`,
    },
    {
      id: 'row4',
      category: intl.formatMessage(messages.dt_row4_text),
      range: `${range.reduceMarkdownCosts * 100}%`,
    },
    {
      id: 'row5',
      category: intl.formatMessage(messages.dt_row5_text),
      range: `${range.reduceShippingCosts * 100}%`,
    },
  ];

  const handleSliders = (
    value: number,
    max: number,
    min: number,
    name: string,
  ) => {
    if (value >= min && value <= max) {
      setInput({
        ...input,
        [name]: value,
      });
      setInvalidMaxMin({
        ...invalidMaxMin,
        [name + 'Max']: false,
        [name + 'Min']: false,
      });
    } else if (value > max) {
      setInvalidMaxMin({
        ...invalidMaxMin,
        [name + 'Max']: true,
      });
      setInput({
        ...input,
        [name]: max,
      });
    } else {
      setInvalidMaxMin({
        ...invalidMaxMin,
        [name + 'Min']: true,
      });
      setInput({
        ...input,
        [name]: min,
      });
    }
  };

  useEffect(() => {
    setInput({
      ...input,
      industryAvgProfit: industry.profitMargin * 100,
      avgMarkDownCost: industry.markdownHigh * 100,
      avgShippingCost: industry.shippingCost * 100,
    });
  }, [industry.label]);

  return (
    <div className="Sterling_ROI widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      {formComplete ? (
        <Thanks
          industry={industry.label}
          estimatedAnnualSale={input.estimatedAnnualSale}
          onlineSalePercentage={input.onlineSalePercentage}
          industryAvgProfit={input.industryAvgProfit}
          avgMarkDownCost={input.avgMarkDownCost}
          avgShippingCost={input.avgShippingCost}
          increaseOnlineSalesTotal={increaseOnlineSalesTotal}
          increaseStoreSalesTotal={increaseStoreSalesTotal}
          reduceReturnsTotal={reduceReturnsTotal}
          reduceMarkDownCostTotal={reduceMarkDownCostTotal}
          reduceShippingCostTotal={reduceShippingCostTotal}
          switchValue={switchValue}
          increaseInSaleTotal={increaseInSaleTotal}
          reductionInCostsTotal={reductionInCostsTotal}
          additionalProfitTotal={additionalProfitTotal}
          estimatedSavings={estimatedSavings}
          bpName={bpName}
        />
      ) : (
        <div className="bx--grid" id="SterlinROIGrid">
          <div className="bx--row mar3">
            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4 sm-mar-left">
              <div className="bx--row">
                <div className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12">
                  {localExist && toggleCountry ? (
                    <ModalWrapper
                      modalLabel={` ${getMessage(
                        messages.foundCountry,
                      )} ${modalLocalCountry}.
                          ${getMessage(
                            messages.accessPage,
                          )} ${modalLocalLanguage} ${modalLocalCurrency}`}
                      modalHeading={`${getMessage(messages.availableIn)}
                          ${modalLocalLanguage} ${getMessage(
                        messages.localCurrencie,
                      )} ${modalLocalCurrency}`}
                      size={'sm'}
                      primaryButtonText={`${getMessage(messages.takeMeThere)}`}
                      secondaryButtonText={`${getMessage(messages.stayHere)} `}
                      buttonTriggerText={` ${getMessage(
                        messages.accessPageCapitalized,
                      )} ${modalLocalLanguage}`}
                      handleOpen={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'SIP ROI/BVA Page',
                          object: 'SIP ROI/BVA Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Modal open ' + modalLocalCountry,
                          location: 'SIP ROI/BVA Estimator',
                          action: '',
                          field: '',
                        });
                      }}
                      handleSubmit={() => {
                        localStorage.setItem('langLocale', selectedLocale);
                        (window.location.href =
                          'https://www.ibm.com/' +
                          selectedLocale +
                          '/products/maximo/pricing'),
                          segmentTracking('CTA Clicked', {
                            pageTitle: 'SIP ROI/BVA Page',
                            object: 'SIP ROI/BVA Page',
                            resultValue: 'CTA clicked',
                            CTA: 'Switched to country ' + modalLocalCountry,
                            location: 'SIP ROI/BVA Estimator',
                            action: '',
                            field: '',
                          });
                        return true;
                      }}
                      onSecondarySubmit={() => {
                        segmentTracking('CTA Clicked', {
                          pageTitle: 'SIP ROI/BVA Page',
                          object: 'SIP ROI/BVA Page',
                          resultValue: 'CTA clicked',
                          CTA: 'Stay here button clicked ' + modalLocalCountry,
                          location: 'SIP ROI/BVA Estimator',
                          action: '',
                          field: '',
                        });
                        return true;
                      }}
                    ></ModalWrapper>
                  ) : null}
                </div>
              </div>
              {/*  Page titles */}
              <div className="bx--row">
                <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                  <div id="pricing">
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_sub_heading">
                          <FormattedMessage {...messages.heading} />
                        </p>
                      </div>
                    </div>
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <p className="page_body_2">
                          <FormattedMessage {...messages.sub_heading} />{' '}
                        </p>
                      </div>
                    </div>
                    <div className="bx--row mar1">
                      <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                        <div className="bx--row expand-slider">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16"></div>
                        </div>
                        <div className="bx--row mar3">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <Tooltip
                              onChange={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Industry dropdown tooltip',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                              direction={'bottom'}
                              tabIndex={0}
                              triggerText={
                                <span className="page_body">
                                  <FormattedMessage
                                    {...messages.dropdown_text}
                                  />
                                </span>
                              }
                            >
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.dropdown_tooltip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                        <div className="bx--row expand-slider">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <Dropdown
                              ariaLabel="Dropdown"
                              id="envizi-industry-dropdown"
                              label={intl.formatMessage(
                                messages.dropdown_label,
                              )}
                              titleText={''}
                              items={answers}
                              selectedItem={industry}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              onChange={(e) => handleDropdown(e.selectedItem!)}
                            />
                          </div>
                        </div>
                        <div className="bx--row mar3">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <Tooltip
                              onChange={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Estimated Annual Sale tooltip',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                              direction={isSmall ? 'left' : 'bottom'}
                              tabIndex={0}
                              triggerText={
                                <span className="page_body">
                                  <FormattedMessage
                                    {...messages.inputField_txt}
                                  />
                                </span>
                              }
                            >
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.inputField_tooltip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                        <div className="bx--row expand-slider mar1">
                          <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                            <TextInput
                              id="AnnualSale-Id"
                              hideLabel={true}
                              labelText={''}
                              value={input.estimatedAnnualSale}
                              defaultValue={estAnnualSale.default}
                              type={'number'}
                              invalid={
                                invalidMaxMin.estimatedAnnualSaleMax ||
                                invalidMaxMin.estimatedAnnualSaleMin
                              }
                              invalidText={
                                invalidMaxMin.estimatedAnnualSaleMax ? (
                                  <span className="slide_text">
                                    <FormattedMessage
                                      {...messages.invalidVmText_1}
                                    />
                                    {` ${estAnnualSale.max}`}
                                    <FormattedMessage
                                      {...messages.invalidVmText_2}
                                    />
                                  </span>
                                ) : invalidMaxMin.estimatedAnnualSaleMin ? (
                                  <span className="slide_text">
                                    <FormattedMessage
                                      {...messages.invalidVmText_3}
                                    />
                                    {` ${estAnnualSale.min}`}
                                    <FormattedMessage
                                      {...messages.invalidVmText_2}
                                    />
                                  </span>
                                ) : null
                              }
                              onBlur={() => {
                                null;
                              }}
                              onChange={(e) => {
                                handleSliders(
                                  Number.parseInt(e.target.value),
                                  estAnnualSale.max,
                                  estAnnualSale.min,
                                  'estimatedAnnualSale',
                                );
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Estimated Annual Sale input',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                            />
                          </div>
                          <div className="bx--col-xlg-5 bx--col-lg-7 bx--col-md-3 bx--col-sm-2">
                            <div className="price_table_comp__bodycopy dollar-mar">
                              USD
                            </div>
                          </div>
                        </div>
                        <div className="bx--row mar3">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <Tooltip
                              onChange={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Percent of sales attributed to online/digital tooltip',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                              direction={'bottom'}
                              tabIndex={0}
                              triggerText={
                                <span className="page_body">
                                  <FormattedMessage {...messages.slider1_txt} />
                                </span>
                              }
                            >
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.slider1_tooltip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                        <div className="bx--row expand-slider">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <Slider
                              id="Sale-Id"
                              min={onlinePctSale.min}
                              max={onlinePctSale.max}
                              inputType={'number'}
                              maxLabel="%"
                              value={input.onlineSalePercentage}
                              invalid={
                                invalidMaxMin.onlineSalePercentageMax ||
                                invalidMaxMin.onlineSalePercentageMin
                              }
                              labelText={
                                invalidMaxMin.onlineSalePercentageMax ? (
                                  <span className="slide_text">
                                    <FormattedMessage
                                      {...messages.invalidVmText_1}
                                    />
                                    {` ${onlinePctSale.max}`}
                                    <FormattedMessage
                                      {...messages.invalidVmText_2}
                                    />
                                  </span>
                                ) : invalidMaxMin.onlineSalePercentageMin ? (
                                  <span className="slide_text">
                                    <FormattedMessage
                                      {...messages.invalidVmText_3}
                                    />
                                    {` ${onlinePctSale.min}`}
                                    <FormattedMessage
                                      {...messages.invalidVmText_2}
                                    />
                                  </span>
                                ) : null
                              }
                              onBlur={() => {
                                null;
                              }}
                              onChange={({ value }) => {
                                handleSliders(
                                  value,
                                  onlinePctSale.max,
                                  onlinePctSale.min,
                                  'onlineSalePercentage',
                                );

                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Online sale percentage slider',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="bx--row">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <div className="expandable-tile-1">
                              <div className="bx--row mar3">
                                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                                  <Tooltip
                                    onChange={() => {
                                      segmentTracking('CTA Clicked', {
                                        pageTitle: 'SIP ROI/BVA Page',
                                        object: 'SIP ROI/BVA Page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Industry Average Gross Profit Margin(%) tooltip',
                                        location: 'SIP ROI/BVA Estimator',
                                        action: '',
                                        field: '',
                                      });
                                    }}
                                    direction={isSmall ? 'left' : 'bottom'}
                                    tabIndex={0}
                                    triggerText={
                                      <span className="page_body">
                                        <FormattedMessage
                                          {...messages.slider2_txt}
                                        />
                                      </span>
                                    }
                                  >
                                    <p className="slide_text">
                                      <FormattedMessage
                                        {...messages.slider2_tooltip}
                                      />
                                    </p>
                                  </Tooltip>
                                </div>
                              </div>
                              <div className="bx--row expand-slider">
                                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                                  {/* Annual Downtime Hours Slider */}
                                  <Slider
                                    id="Industry-Id"
                                    min={industryAvg.min}
                                    max={industryAvg.max}
                                    maxLabel="%"
                                    value={input.industryAvgProfit}
                                    inputType={'number'}
                                    onBlur={() => {
                                      null;
                                    }}
                                    invalid={
                                      invalidMaxMin.industryAvgProfitMax ||
                                      invalidMaxMin.industryAvgProfitMin
                                    }
                                    labelText={
                                      invalidMaxMin.industryAvgProfitMax ? (
                                        <span className="slide_text">
                                          <FormattedMessage
                                            {...messages.invalidVmText_1}
                                          />
                                          {` ${industryAvg.max}`}
                                          <FormattedMessage
                                            {...messages.invalidVmText_2}
                                          />
                                        </span>
                                      ) : invalidMaxMin.industryAvgProfitMin ? (
                                        <span className="slide_text">
                                          <FormattedMessage
                                            {...messages.invalidVmText_3}
                                          />
                                          {` ${industryAvg.min}`}
                                          <FormattedMessage
                                            {...messages.invalidVmText_2}
                                          />
                                        </span>
                                      ) : null
                                    }
                                    onChange={({ value }) => {
                                      handleSliders(
                                        value,
                                        industryAvg.max,
                                        industryAvg.min,
                                        'industryAvgProfit',
                                      );
                                      segmentTracking('CTA Clicked', {
                                        pageTitle: 'SIP ROI/BVA Page',
                                        object: 'SIP ROI/BVA Page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Industry average profit slider',
                                        location: 'ROI/BVA Estimator',
                                        action: '',
                                        field: '',
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="bx--row mar3">
                                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                                  <Tooltip
                                    onChange={() => {
                                      segmentTracking('CTA Clicked', {
                                        pageTitle: 'SIP ROI/BVA Page',
                                        object: 'ROI/BVA Page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Average markdown costs tooltip',
                                        location: 'ROI/BVA Estimator',
                                        action: '',
                                        field: '',
                                      });
                                    }}
                                    direction={'bottom'}
                                    tabIndex={0}
                                    triggerText={
                                      <span className="page_body">
                                        <FormattedMessage
                                          {...messages.slider3_txt}
                                        />
                                      </span>
                                    }
                                  >
                                    <p className="slide_text">
                                      <FormattedMessage
                                        {...messages.slider3_tooltip}
                                      />
                                    </p>
                                  </Tooltip>
                                </div>
                              </div>
                              {/* % Downtime Reduction Slider */}
                              <div className="bx--row expand-slider">
                                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                                  <Slider
                                    id="MarkDown-Id"
                                    min={markdownAvg.min}
                                    max={markdownAvg.max}
                                    maxLabel="%"
                                    inputType={'number'}
                                    value={input.avgMarkDownCost}
                                    invalid={
                                      invalidMaxMin.avgMarkDownCostMax ||
                                      invalidMaxMin.avgMarkDownCostMin
                                    }
                                    labelText={
                                      invalidMaxMin.avgMarkDownCostMax ? (
                                        <span className="slide_text">
                                          <FormattedMessage
                                            {...messages.invalidVmText_1}
                                          />
                                          {` ${markdownAvg.max}`}
                                          <FormattedMessage
                                            {...messages.invalidVmText_2}
                                          />
                                        </span>
                                      ) : invalidMaxMin.avgMarkDownCostMin ? (
                                        <span className="slide_text">
                                          <FormattedMessage
                                            {...messages.invalidVmText_3}
                                          />
                                          {` ${markdownAvg.min}`}
                                          <FormattedMessage
                                            {...messages.invalidVmText_2}
                                          />
                                        </span>
                                      ) : null
                                    }
                                    onBlur={() => {
                                      null;
                                    }}
                                    onChange={({ value }) => {
                                      handleSliders(
                                        value,
                                        markdownAvg.max,
                                        markdownAvg.min,
                                        'avgMarkDownCost',
                                      );
                                      segmentTracking('CTA Clicked', {
                                        pageTitle: 'SIP ROI/BVA Page',
                                        object: 'SIP ROI/BVA Page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Average markdown costs slider',
                                        location: 'SIP ROI/BVA Estimator',
                                        action: '',
                                        field: '',
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="bx--row mar3">
                                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                                  <Tooltip
                                    onChange={() => {
                                      segmentTracking('CTA Clicked', {
                                        pageTitle: 'SIP ROI/BVA Page',
                                        object: 'SIP ROI/BVA Page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Average shipping costs tooltip',
                                        location: 'SIP ROI/BVA Estimator',
                                        action: '',
                                        field: '',
                                      });
                                    }}
                                    direction={'bottom'}
                                    tabIndex={0}
                                    triggerText={
                                      <span className="page_body">
                                        <FormattedMessage
                                          {...messages.slider4_txt}
                                        />
                                      </span>
                                    }
                                  >
                                    <p className="slide_text">
                                      <FormattedMessage
                                        {...messages.slider4_tooltip}
                                      />
                                    </p>
                                  </Tooltip>
                                </div>
                              </div>
                              <div className="bx--row expand-slider">
                                <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                                  <Slider
                                    id="Shipping-Id"
                                    min={shippingAvg.min}
                                    max={shippingAvg.max}
                                    maxLabel="%"
                                    inputType={'number'}
                                    value={input.avgShippingCost}
                                    invalid={
                                      invalidMaxMin.avgShippingCostMax ||
                                      invalidMaxMin.avgShippingCostMin
                                    }
                                    labelText={
                                      invalidMaxMin.avgShippingCostMax ? (
                                        <span className="slide_text">
                                          <FormattedMessage
                                            {...messages.invalidVmText_1}
                                          />
                                          {` ${shippingAvg.max}`}
                                          <FormattedMessage
                                            {...messages.invalidVmText_2}
                                          />
                                        </span>
                                      ) : invalidMaxMin.avgShippingCostMin ? (
                                        <span className="slide_text">
                                          <FormattedMessage
                                            {...messages.invalidVmText_3}
                                          />
                                          {` ${shippingAvg.min}`}
                                          <FormattedMessage
                                            {...messages.invalidVmText_2}
                                          />
                                        </span>
                                      ) : null
                                    }
                                    onBlur={() => {
                                      null;
                                    }}
                                    onChange={({ value }) => {
                                      handleSliders(
                                        value,
                                        shippingAvg.max,
                                        shippingAvg.min,
                                        'avgShippingCost',
                                      );

                                      segmentTracking('CTA Clicked', {
                                        pageTitle: 'SIP ROI/BVA Page',
                                        object: 'SIP ROI/BVA Page',
                                        resultValue: 'CTA clicked',
                                        CTA: 'Average shipping costs slider',
                                        location: 'SIP ROI/BVA Estimator',
                                        action: '',
                                        field: '',
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bx--row">
                          <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                            <Tooltip
                              onChange={() => {
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Low/Medium/High switch tooltip',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                              direction={'bottom'}
                              tabIndex={0}
                              triggerText={
                                <span className="page_body">
                                  <FormattedMessage
                                    {...messages.switch_label}
                                  />
                                </span>
                              }
                            >
                              <p className="slide_text">
                                <FormattedMessage
                                  {...messages.switch_tooltip}
                                />
                              </p>
                            </Tooltip>
                            <ContentSwitcher
                              className="content-switcher mar8"
                              selectedIndex={rangeIndex}
                              onChange={(obj) => {
                                const { name } = obj;
                                if (name === 'Low') {
                                  setRangeIndex(0);
                                  setRange(lowPct);
                                  setSwitchValue('Low');
                                } else if (name === 'Medium') {
                                  setRangeIndex(1);
                                  setRange(medPct);
                                  setSwitchValue('Medium');
                                } else if (name === 'High') {
                                  setRangeIndex(2);
                                  setRange(hightPct);
                                  setSwitchValue('High');
                                }
                                segmentTracking('CTA Clicked', {
                                  pageTitle: 'SIP ROI/BVA Page',
                                  object: 'SIP ROI/BVA Page',
                                  resultValue: 'CTA clicked',
                                  CTA: 'Annual Pro-forma Benefit selected',
                                  location: 'SIP ROI/BVA Estimator',
                                  action: '',
                                  field: '',
                                });
                              }}
                            >
                              <Switch
                                name={'Low'}
                                text={intl.formatMessage(messages.switch1_txt)}
                              />
                              <Switch
                                name={'Medium'}
                                text={intl.formatMessage(messages.switch2_txt)}
                              />
                              <Switch
                                name={'High'}
                                text={intl.formatMessage(messages.switch3_txt)}
                              />
                            </ContentSwitcher>
                          </div>
                        </div>
                        <div className="bx--row mar8">
                          <div className="bx--col-lg-16 bx--col-md-9 bx--col-sm-5 mar-left1">
                            <DataTable size="md" headers={headers} rows={rows}>
                              {({
                                rows,
                                headers,
                                getTableProps,
                                getHeaderProps,
                                getRowProps,
                                getTableContainerProps,
                              }: DataTableCustomRenderProps) => (
                                <TableContainer {...getTableContainerProps()}>
                                  <Table {...getTableProps()}>
                                    <TableHead className="header-bakground">
                                      <TableRow>
                                        {headers.map((header) => (
                                          <TableHeader
                                            {...getHeaderProps({ header })}
                                          >
                                            {header.header}
                                          </TableHeader>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {rows.map((row) => (
                                        <TableRow {...getRowProps({ row })}>
                                          {row.cells.map((cell) => (
                                            <TableCell key={cell.id}>
                                              {cell.value}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                            </DataTable>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div id="footnote" className="div-only-lg mar4">
                    <div className="bx--row">
                      <div className="bx--col-lg-16">
                        <Footnote numNote={1} />
                        <div className="footnote_text">
                          <FormattedMessage {...messages.disclaimer_text} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bx--col-lg-1"></div>
                <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16 mar-left2">
                  <div className="bx--row" id="stickyNav">
                    <div className="Sterling_ROI__sticky-nav price_table_comp">
                      {/* Table with results  */}
                      <div id="graphSummary">
                        <div style={{ backgroundColor: '#f4f4f4' }}>
                          <div style={{ float: 'right' }}>
                            <p
                              id="widget.estimated-cost"
                              className="Sterling_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                            >
                              <FormattedMessage
                                {...messages.additional_profit}
                              />
                              <sup className="Sterling_ROI__sup">1</sup>
                            </p>
                            <p className="padding_1 price_table_comp__price padding_24_1">
                              {`${additionalProfitTotal}*`}
                            </p>
                          </div>
                          <p
                            id="widget.estimated-cost"
                            className="Sterling_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.increase_sale_text}
                            />{' '}
                            <sup className="Sterling_ROI__sup">1</sup>
                          </p>
                          <p className="padding_1 price_table_comp__price padding_24_1">
                            {`${increaseInSaleTotal}*`}
                          </p>
                          <p
                            id="widget.estimated-cost"
                            className="Sterling_ROI__ibm-type-e estmt-cost price_table_comp__bodycopy"
                          >
                            <FormattedMessage
                              {...messages.reduction_cost_text}
                            />{' '}
                            <sup className="Sterling_ROI__sup">1</sup>
                          </p>
                          <p className="padding_1 price_table_comp__price padding_24_1">
                            {`${reductionInCostsTotal}*`}
                          </p>
                          <div>
                            <DonutChart
                              data={state.data}
                              options={state.options}
                            ></DonutChart>
                          </div>
                        </div>
                      </div>
                      {totalEstimateSaving >= 10000 && (
                        <div className="mar4">
                          <div id="tableSummary">
                            <table className="cds--data-table cds--data-table--sm">
                              <thead>
                                <tr>
                                  <th scope="col" colSpan={2}>
                                    <div className="price_table_comp__heading">
                                      <FormattedMessage
                                        {...messages.category}
                                      />
                                    </div>
                                  </th>
                                  <th></th>
                                  <th scope="col">
                                    <div className="price_table_comp__heading">
                                      <FormattedMessage {...messages.benefit} />
                                      {` (${'USD'})`}
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody aria-live="polite">
                                <tr>
                                  <td colSpan={2}>
                                    <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                      <FormattedMessage
                                        {...messages.catg1_text}
                                      />
                                    </div>
                                  </td>
                                  <td></td>
                                  <td>
                                    <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                      'USD',
                                      increaseOnlineSalesTotal,
                                    )}`}</div>
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={2}>
                                    <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                      <FormattedMessage
                                        {...messages.catg2_text}
                                      />
                                    </div>
                                  </td>
                                  <td></td>
                                  <td>
                                    <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                      'USD',
                                      increaseStoreSalesTotal,
                                    )}`}</div>
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={2}>
                                    <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                      <FormattedMessage
                                        {...messages.catg3_text}
                                      />
                                    </div>
                                  </td>
                                  <td></td>
                                  <td>
                                    <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                      'USD',
                                      reduceReturnsTotal,
                                    )}`}</div>
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={2}>
                                    <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                      <FormattedMessage
                                        {...messages.catg4_text}
                                      />
                                    </div>
                                  </td>
                                  <td></td>
                                  <td>
                                    <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                      'USD',
                                      reduceMarkDownCostTotal,
                                    )}`}</div>
                                  </td>
                                </tr>
                                <tr>
                                  <td colSpan={2}>
                                    <div className="price_table_comp__requirement price_table_comp_color padding_8">
                                      <FormattedMessage
                                        {...messages.catg5_text}
                                      />
                                    </div>
                                  </td>
                                  <td></td>
                                  <td>
                                    <div className="price_table_comp__quantity padding_8">{`${removeSymbol(
                                      'USD',
                                      reduceShippingCostTotal,
                                    )}`}</div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div className="bx--row mar3_b">
                            <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-16">
                              <div id="price_toggle" className="padding_24_2">
                                <Button
                                  id="request_quote_btn"
                                  onClick={() => {
                                    segmentTracking('CTA Clicked', {
                                      pageTitle: 'SIP ROI/BVA Page',
                                      object: 'SIP ROI/BVA Page',
                                      resultValue: 'CTA clicked',
                                      CTA: 'Request a quote',
                                      location: 'SIP ROI/BVA Estimator',
                                      action: '',
                                      field: '',
                                    });
                                    initUrxForm(
                                      environment,
                                      URX_FORM.INSTANCE_ID,
                                      URX_FORM.FORM_ID,
                                      urxlang as IBMLocale,
                                      companyId,
                                    );
                                    setDrawerOpen(true);
                                    setCookie();
                                  }}
                                >
                                  <FormattedMessage {...messages.requestBtn} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div id="footnote" className="div-only-media">
                      <div className="bx--row mar4">
                        <div className="bx--col-lg-9">
                          <div>
                            <Footnote numNote={1} />
                          </div>
                          <div className="footnote_text">
                            <FormattedMessage {...messages.disclaimer_text} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <FormModal
        pageTitle="SIP ROI/BVA Page"
        object="SIP ROI/BVA Page"
        productTitle={'SIP ROI/BVA price estimator'}
        pageUrl={''}
        location={'SIP ROI/BVA Estimator'}
        bpName={bpName}
        logo={logo}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        id={'urx-form'}
      ></FormModal>
    </div>
  );
};

export default SterlingBVAROI;
